import classNames from "classnames";
import { useState } from "react";
import { AppButton } from "../../shared/components/AppButton";
import { AppIconButton } from "../../shared/components/AppIconButton";
import { AppLoading } from "../../shared/components/AppLoading";
import { EraserIcon } from "../../shared/icons/EraserIcon";
import { SearchIcon } from "../../shared/icons/SearchIcon";
import { ChatModal } from "./Components/ChatModal";
import { SearchPanel } from "./Components/SearchPanel";
import { AddButton } from "./Components/SidebarButtons/AddButton";
import { AskGPTButton } from "./Components/SidebarButtons/AskGPTButton";
import { SoundToTextButton } from "./Components/SidebarButtons/SoundToTextButton";
import { SidebarFooter } from "./Components/SidebarFooter";
import { SidebarList } from "./Components/SidebarList";
import { useSidebarSearch } from "./hook/use-sidebar-search";
import type { MenuItems, SidebarModalsType } from "./types";
import { StorageScope } from "../../services/api/instances/storageApi/types/service_types";
import { SidebarResizableWrapper } from "../AppSidebar/Components/SidebarResizableWrapper";
import styles from "./styles.module.css";
import { AudioTranscription } from "../../features/Modals/AudioTranscription";
import { useAppModals } from "../../app/state/modalSlice/hook/use-app-modals";

interface Props {
  menuItems: MenuItems | null;
  onSelect: (id: string) => void;
  onOpenModal?: (id: string) => void;
  onOpenModalNameChange?: (id: string, name: string) => void;
  defaultName?: string;
  selectedId?: string | number | null;
  onAdd?: () => void;
  onDelete?: (id: string, name: string) => void;
  handleCopyObject?: () => void;
  isSendDisabled?: boolean;
  isSidebarDisabled?: boolean;
  isLoading?: boolean;
  hasHeader?: boolean;
  headerAdditionalContent?: React.ReactNode;
  footerAdditionalContent?: React.ReactNode;
  isTransfer?: boolean;
  onOpenMenu?: (
    event: React.MouseEvent<HTMLElement>,
    id: string,
    name: string,
  ) => void;
  onCopyObject?: (id: string) => void;
  hasMenu?: boolean;
  className?: string;
  service: StorageScope;
}

export const AppSidebar: React.FC<Props> = ({
  menuItems,
  defaultName,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  onOpenModalNameChange,
  isSendDisabled,
  isSidebarDisabled,
  isLoading,
  headerAdditionalContent,
  footerAdditionalContent,
  hasHeader = true,
  isTransfer,
  onOpenMenu,
  hasMenu,
  onOpenModal,
  onCopyObject,
  service,
  className,
}) => {
  const [openSidebarModal, setOpenSidebarModal] = useState<string>("");
  const {
    isOpenSearchPanel,
    searchQuery,
    searchingMenuItems,
    handleOpenSearchPanel,
    clearSearchInput,
    handleChangeSearchValue,
  } = useSidebarSearch(menuItems || [], onSelect);
  const { handleOpenModal, handleCloseModal } = useAppModals();
  const handleSidebarModalOpen = (type: SidebarModalsType) => {
    switch (type) {
      case "askGpt":
        setOpenSidebarModal("askGpt");
        return;
      case "soundToText":
        setOpenSidebarModal("soundToText");
        return;
      case "staticToken":
        setOpenSidebarModal("staticToken");
        return;
      default:
        return;
    }
  };

  const handleSidebarModalClose = () => {
    setOpenSidebarModal("");
  };

  return (
    <>
      <SidebarResizableWrapper
        service={service}
        isSidebarOpen
        minWidth={260}
        maxWidth={500}
      >
        {hasHeader && (
          <div
            className={classNames(
              styles["sidebar-header"],
              isOpenSearchPanel && styles["open-header"],
            )}
          >
            <div className={styles["constant-block"]}>
              {onAdd && (
                <div className={styles["button-container"]}>
                  <AddButton onAdd={onAdd} isDisabled={isSendDisabled} />
                </div>
              )}
              {isLoading && (
                <AppLoading className={styles.loader} settingSize={16} />
              )}
              {}
              {headerAdditionalContent && (
                <div className={styles["sidebar-header-actions"]}>
                  {service && (
                    <AppButton
                      buttonType="text"
                      onClick={handleOpenSearchPanel}
                      className={styles["search-button"]}
                    >
                      <SearchIcon />
                    </AppButton>
                  )}
                  {headerAdditionalContent}
                </div>
              )}
            </div>
            {isOpenSearchPanel && (
              <div className={styles["search-container"]}>
                <SearchPanel
                  value={searchQuery || ""}
                  onChange={handleChangeSearchValue}
                  autoFocus={isOpenSearchPanel}
                />
                <AppIconButton
                  className={styles["clear-search-input"]}
                  onClick={clearSearchInput}
                >
                  <EraserIcon />
                </AppIconButton>
              </div>
            )}
          </div>
        )}
        <div className={styles["sidebar-body"]}>
          <ul className={styles.list}>
            <SidebarList
              menuItems={
                searchingMenuItems !== null && searchingMenuItems.length > 0
                  ? searchingMenuItems
                  : (menuItems as MenuItems) || []
              }
              onSelect={onSelect}
              isTransfer={isTransfer}
              selectedId={selectedId as string}
              defaultName={defaultName}
              onDelete={onDelete}
              hasMenu={hasMenu}
              onOpenMenu={onOpenMenu}
              onOpenModal={onOpenModal}
              onOpenModalNameChange={onOpenModalNameChange}
              onCopyObject={onCopyObject && onCopyObject}
              service={service}
              isSendDisabled={isSendDisabled}
            />
          </ul>
        </div>
        <SidebarFooter>
          {footerAdditionalContent && footerAdditionalContent}
          <AskGPTButton openAskGPT={handleSidebarModalOpen} />
          <SoundToTextButton
            openSoundToText={() => handleOpenModal("sound_to_text_modal", null)}
          />
        </SidebarFooter>
      </SidebarResizableWrapper>
      <AudioTranscription closeModal={handleCloseModal} />
      <ChatModal
        open={openSidebarModal === "askGpt"}
        closeModal={handleSidebarModalClose}
      />
    </>
  );
};
